import React, { useEffect, useRef } from 'react';
import { Feed } from '../Feed';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useGetActivityFeed } from '@reactQuery/activity/activity';
import { Loader } from '../Loader';
import './styles.css';
import { ActivityRenderer } from './components/ActivityRenderer';
import { TimeProvider } from '@context/TimeContext';
import { EndOfFeed } from '../EndOfFeed';
import { useEcho } from '@laravel/echo-react';
import { Activity } from '@api/activityApi/interfaces';
import { isDebug } from '@helpers/envHelper';
import {
  WEB_SOCKET_CHANNEL_MESSAGES,
  WEB_SOCKET_CHANNELS,
} from '@constants/web-sockets';
import { useQueryClient } from '@tanstack/react-query';
import { useBroadcasting } from '@hooks/useBroadcasting';
import { IActivityFeed } from './interfaces';
import { activityMatchesFeedScope } from './helpers';

const ACTIVITY_FEED_PER_PAGE = 10;

export const ActivityFeed: React.FC<IActivityFeed> = ({
  scope = null,
}): JSX.Element => {
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const queryClient = useQueryClient();
  const { channel } = useBroadcasting();

  const queryKey = ['activity-feed', { perPage: ACTIVITY_FEED_PER_PAGE, scope }];

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetActivityFeed({ perPage: ACTIVITY_FEED_PER_PAGE, scope });

  const activities = data?.pages.flatMap((page) => page.data) ?? [];
  const portalActivityChannel = channel(WEB_SOCKET_CHANNELS.portalActivityFeed);

  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey,
      });
    };
  }, [queryClient, scope]);

  useEffect(() => {
    if (!loaderRef.current || !hasNextPage) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        fetchNextPage();
      }
    });

    observer.observe(loaderRef.current);

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage]);

  useEcho<Activity>(
    portalActivityChannel,
    WEB_SOCKET_CHANNEL_MESSAGES.portalActivityFeed.created,
    (newActivity) => {
      if (!activityMatchesFeedScope(newActivity, scope)) {
        return;
      }

      if (isDebug()) {
        console.log(`Echo: Adding activity: ${newActivity.id}`);
      }

      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;

        const firstPage = old.pages[0];

        return {
          ...old,
          pages: [
            {
              ...firstPage,
              data: [newActivity, ...firstPage.data],
            },
            ...old.pages.slice(1),
          ],
        };
      });

      if (scope && newActivity.scopes?.includes('portal')) {
        queryClient.setQueryData(
          ['activity-feed', { perPage: ACTIVITY_FEED_PER_PAGE, scope: null }],
          (old: any) => {
            if (!old) return old;

            const firstPage = old.pages[0];

            return {
              ...old,
              pages: [
                {
                  ...firstPage,
                  data: [newActivity, ...firstPage.data],
                },
                ...old.pages.slice(1),
              ],
            };
          }
        );
      }
    }
  );

  useEcho<Activity>(
    portalActivityChannel,
    WEB_SOCKET_CHANNEL_MESSAGES.portalActivityFeed.updated,
    (updatedActivity) => {
      const matchesCurrentScope = activityMatchesFeedScope(
        updatedActivity,
        scope
      );

      if (!matchesCurrentScope) {
        if (scope) {
          queryClient.setQueryData(queryKey, (old: any) => {
            if (!old) return old;

            return {
              ...old,
              pages: old.pages.map((page: any) => ({
                ...page,
                data: page.data.filter(
                  (activity: Activity) => activity.id !== updatedActivity.id
                ),
              })),
            };
          });
        }

        return;
      }

      if (isDebug()) {
        console.log(`Echo: Updating activity: ${updatedActivity.id}`);
      }

      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;

        const allActivities = old.pages.flatMap((p: any) => p.data);

        const filtered = allActivities.filter(
          (a: Activity) => a.id !== updatedActivity.id
        );

        filtered.push(updatedActivity);

        filtered.sort(
          (a: Activity, b: Activity) =>
            new Date(b.date).getTime() - new Date(a.date).getTime()
        );

        const newPages = [];

        for (let i = 0; i < filtered.length; i += ACTIVITY_FEED_PER_PAGE) {
          newPages.push({
            ...old.pages[0],
            data: filtered.slice(i, i + ACTIVITY_FEED_PER_PAGE),
          });
        }

        return {
          ...old,
          pages: newPages,
        };
      });
    }
  );

  useEcho<Activity>(
    portalActivityChannel,
    WEB_SOCKET_CHANNEL_MESSAGES.portalActivityFeed.deleted,
    (deletedActivity) => {
      if (isDebug()) {
        console.log(`Echo: Deleting activity: ${deletedActivity.id}`);
      }

      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;

        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: page.data.filter(
              (activity: Activity) => activity.id !== deletedActivity.id
            ),
          })),
        };
      });
    }
  );

  return (
    <TimeProvider>
      <Feed
        sx={{
          '& .activities-wrapper > div': {
            mb: '10px',
          },
        }}
      >
        <TransitionGroup className="activities-wrapper">
          {activities.map((activity) => {
            return (
              <CSSTransition
                key={activity.id}
                timeout={300}
                classNames="activity"
              >
                <ActivityRenderer activity={activity} />
              </CSSTransition>
            );
          })}
        </TransitionGroup>

        {(hasNextPage || isLoading) && (
          <div ref={loaderRef} style={{ height: '40px' }}>
            {(isLoading || isFetchingNextPage) && <Loader />}
          </div>
        )}

        {!hasNextPage && data && <EndOfFeed />}
      </Feed>
    </TimeProvider>
  );
};
